import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type Dispatch,
  type SetStateAction
} from 'react';
import {
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams
} from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clusteringApi, {
  useLazyGetClusteringDetailsQuery
} from '@/app/KeywordResearch/api/clusteringApi';
import { useDispatch } from 'react-redux';
import Tree from '../components/keyword-clustring/components/Tree';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import _ from 'lodash';
import ClusterTreeShell from '../components/keyword-clustring/components/ClusterTreeShell';
import ClusterViewModeSwitcher from '../components/keyword-clustring/components/ClusterViewModeSwitcher';
import ClusteringProcessingState from '../components/keyword-clustring/components/ClusteringProcessingState';
import sampleCluster from '../components/keyword-clustring/data/sample-cluster.json';
import type { ClusterData, HierarchicalNode } from '../components/keyword-clustring/types';
import type {
  ClusteringDetailsData,
  ClusteringDetailsResponse
} from '@/app/KeywordResearch/types/clusteringApiTypes';
import useDomain from '@/hooks/useDomain';

const DEMO_QUERY = 'demo';

type BreadcrumbItem = { text: string; href: string };
type OutletContext = [
  BreadcrumbItem[],
  Dispatch<SetStateAction<BreadcrumbItem[]>>
];

type ClusterViewModeId = 'flow' | 'paths' | 'card' | 'basic' | 'radial';

type AgGridSkeletonProps = {
  height: string;
};

const TypedAgGridSkeleton = AgGridSkeleton as ComponentType<AgGridSkeletonProps>;

const CLUSTER_VIEW_MODES = [
  { id: 'flow', label: 'درختی', icon: 'diagram-3' },
  { id: 'paths', label: 'جدولی', icon: 'list-ul' },
  { id: 'card', label: 'کارتی', icon: 'subtract' },
  { id: 'basic', label: 'پایه', icon: 'folder2' },
  { id: 'radial', label: 'مرکزی', icon: 'bullseye' }
];

function getHierarchicalTree(
  data: ClusteringDetailsData | null
): HierarchicalNode | null {
  if (!data) return null;
  return (data.ai_clustering ?? data.hierarchical ?? null) as HierarchicalNode | null;
}

function toClusterData(data: ClusteringDetailsData): ClusterData {
  return {
    task_id: data.task_id,
    user_id: data.user_id,
    stem: data.stem,
    created_at:
      data.created_at !== undefined ? String(data.created_at) : undefined,
    hierarchical: getHierarchicalTree(data)
  };
}

const ClusteringDetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { domain_slug, domain } = useDomain();
  const [, setBreadcrumbData] = useOutletContext<OutletContext>();
  useEffect(() => {
    setBreadcrumbData([{ text: 'خوشه بندی', href: '#' }]);
  }, [setBreadcrumbData]);

  const pageRef = useRef<HTMLDivElement>(null);
  const [viewChartMode, setViewChartMode] =
    useState<ClusterViewModeId>('flow');
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const useDemoData = searchParams.get(DEMO_QUERY) === '1';
  const [getClusteringDetail, { isLoading, isFetching }] =
    useLazyGetClusteringDetailsQuery();

  const handleNewClustering = () => {
    if (!domain_slug || !domain) return;
    navigate(`/keyword-research/clustering/${domain_slug}/${domain}`);
  };

  const [detailsData, setDetailsData] = useState<ClusteringDetailsData | null>(
    null
  );

  const fetchDetails = useCallback(() => {
    if (!id || useDemoData) return;
    getClusteringDetail(id)
      .unwrap()
      .then(res => {
        setDetailsData(res.data);
      })
      .catch(() => {
        setDetailsData(null);
      });
  }, [getClusteringDetail, id, useDemoData]);

  const handleProcessingComplete = useCallback(() => {
    dispatch(clusteringApi.util.invalidateTags(['ClusteringHistory']));
    fetchDetails();
  }, [dispatch, fetchDetails]);

  useEffect(() => {
    if (useDemoData) {
      const sample = sampleCluster as ClusteringDetailsResponse;
      setDetailsData(sample.data);
      return;
    }
    fetchDetails();
  }, [fetchDetails, useDemoData]);

  const handleViewDemo = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.set(DEMO_QUERY, '1');
    window.open(url.toString(), '_blank', 'noopener,noreferrer');
  }, []);

  const hierarchical = useMemo(
    () => getHierarchicalTree(detailsData),
    [detailsData]
  );

  const clusterData = useMemo(
    () => (detailsData ? toClusterData(detailsData) : null),
    [detailsData]
  );

  const handleHierarchicalChange = useCallback(
    (next: HierarchicalNode) => {
      setDetailsData(prev => {
        if (!prev) return prev;
        if (prev.ai_clustering) {
          return { ...prev, ai_clustering: next, hierarchical: next };
        }
        return { ...prev, hierarchical: next };
      });
    },
    []
  );

  const shellModes =
    viewChartMode === 'flow' ||
    viewChartMode === 'paths' ||
    viewChartMode === 'card';

  const hasTree = Boolean(detailsData && hierarchical);
  const isProcessing =
    Boolean(detailsData) &&
    (_.isEmpty(detailsData) || !hierarchical);

  return (
    <div ref={pageRef} className="cluster-page d-flex flex-column">
      <div className="cluster-page__switcher position-relative d-flex align-items-center justify-content-center mt-3 mb-3 px-2">
        <Button
          variant="falcon-primary"
          size="sm"
          className="position-absolute start-0 d-flex align-items-center gap-1 py-1 px-2 fs--1"
          onClick={handleNewClustering}
        >
          <FontAwesomeIcon icon="plus" className="fs--2" />
          خوشه‌بندی جدید
        </Button>
        <ClusterViewModeSwitcher
          value={viewChartMode}
          onChange={mode => setViewChartMode(mode as ClusterViewModeId)}
          modes={CLUSTER_VIEW_MODES}
          aria-label="نوع نمایش خوشه‌بندی"
        />
      </div>
      {hasTree && hierarchical ? (
        shellModes && clusterData ? (
          <ClusterTreeShell
            mode={viewChartMode}
            data={clusterData}
            fullscreenRootRef={pageRef}
            onHierarchicalChange={handleHierarchicalChange}
          />
        ) : (
          <>
            {viewChartMode === 'basic' && (
              <Tree title="درخت پایه" data={hierarchical} />
            )}
            {viewChartMode === 'radial' && (
              <Tree data={hierarchical} layout="radial" title="درخت مرکزی" />
            )}
          </>
        )
      ) : isProcessing ? (
        <ClusteringProcessingState
          taskId={id}
          onRefresh={handleProcessingComplete}
          onViewDemo={handleViewDemo}
          isRefreshing={isLoading || isFetching}
        />
      ) : (
        <TypedAgGridSkeleton height="700px" />
      )}
    </div>
  );
};

export default ClusteringDetails;
